import CommandCenterService from "../../../../../../service/CommandCenterService";
import { showToastMessage, uuidv4 } from "../../../../../../utils/commonFunctions";
import { ConsoleFilterOptionType } from "../../../../types";
import type { AllFilterType } from "../../../../../CommonComponents/custom_filter/types";

//// ----------------------------
//// Time and Formatting Utilities
//// ----------------------------

/**
 * Converts a Unix timestamp (seconds) to UTC ISO string.
 */
export const get_formated_time = (time: number) => {
  const date = new Date(time * 1000);
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
};

/** Console filter options from API */
export interface ConsoleFilterOptions {
  SaIdFilter?: { Label: string; [key: string]: unknown }[];
  DaIdFilter?: { Label: string; [key: string]: unknown }[];
  TMReceivedTypeFilter?: string[];
  GenericTCTMTypeFilter?: string[];
  TCTMIdFilter?: unknown[];
  BandFilter?: string[];
  [key: string]: unknown;
}

/** Optional filter extensions for validate/reporting views that reuse console APIs. */
export type ConsoleQueryFilters = AllFilterType & {
  validateSelectionFilter?: {
    testPlanResultId?: string | null;
    testCaseResultId?: string | null;
    /** Legacy fallback fields while callers migrate to result-id names. */
    testPlanId?: string | null;
    testCaseId?: string | null;
  };
  payloadOverrides?: Record<string, unknown>;
}

/** Shared operator config for advance filter dropdowns (equals / not equals) */
const ADVANCE_OPERATOR = {
  enabledSearch: false,
  multiselection: false,
  placeholder: "Operator",
  options: [
    { label: "= Equals", value: "= Equals" },
    { label: "≠ not Equals", value: "≠ not Equals" },
  ],
};

//// ----------------------------
//// Filter Option Utilities
//// ----------------------------

/**
 * Returns the filter options structure for the filter UI.
 */
export const getFilterOption = (filterOptions) => {
  const operator = {
    enabledSearch: false,
    multiselection: false,
    placeholder: 'Operator',
    options: [
      { label: '= Equals', value: '= Equals' },
      { label: '≠ not Equals', value: '≠ not Equals' },
    ]
  };

  const options = {
    enabledSearch: true,
    placeholder: 'Property',
    options: [
      {
        label: 'Source',
        value: 'Source',
        operator,
        properties: {
          enabledSearch: true,
          multiselection: true,
          placeholder: 'Value',
          options: filterOptions?.SaIdFilter?.map((option) => ({
            label: option?.Label,
            value: option
          }))
        }
      },
      {
        label: 'Destination',
        value: 'Destination',
        operator,
        properties: {
          enabledSearch: true,
          multiselection: true,
          placeholder: 'Value',
          options: filterOptions?.DaIdFilter?.map((option) => ({
            label: option?.Label,
            value: option
          }))
        }
      },
      {
        label: 'TM Received Type',
        value: 'TM Received Type',
        operator,
        properties: {
          enabledSearch: true,
          multiselection: true,
          placeholder: 'Value',
          options: filterOptions?.TMReceivedTypeFilter?.map((option) => ({
            label: option,
            value: option
          }))
        }
      },
      {
        label: 'Command Type',
        value: 'Command Type',
        operator,
        properties: {
          enabledSearch: true,
          multiselection: true,
          placeholder: 'Value',
          options: filterOptions?.GenericTCTMTypeFilter?.map((option) => ({
            label: option,
            value: option
          }))
        }
      },
      {
        label: 'TC/TM ID',
        value: 'TC/TM ID',
        operator,
        properties: {
          enabledSearch: true,
          multiselection: false,
          valueField: 'text',
          placeholder: 'Value',
          options: filterOptions?.TCTMIdFilter?.map((option) => ({
            label: option,
            value: option
          }))
        }
      },
    ]
  };
  return options;
};

/**
 * Returns an array of band options for the filter UI.
 */
export const getBands = (filterOptions) => {
  const bands: { label: string, value: string }[] = [];
  filterOptions?.BandFilter?.forEach((band) => {
    const bandObj = { label: band.split('-')[0], value: band.split('-')[0] };
    bands.push(bandObj);
  });
  return bands;
};

//// ----------------------------
//// Filter Data Conversion
//// ----------------------------

/**
 * Converts UI filter state to the API payload format.
 */
export const convertFiltersToApiFormat = (filters) => {
  // Time filter
  const { startTime, endTime } = filters?.timeFilter || {};
  const epochSecondsStart = startTime ? startTime : -1;
  const epochSecondsEnd = endTime ? endTime : -1;

  // Source, destination, and TC/TM ID filters
  const sourceFilter = filters?.advanceFilters?.find(f => f.properties === 'Source');
  const sourceNumbers = sourceFilter?.value?.map(val => Number(val.split('-')[0]));
  const destinationFilter = filters?.advanceFilters?.find(f => f.properties === 'Destination');
  const destinationNumbers = destinationFilter?.value?.map(val => Number(val.split('-')[0]));
  const tcTmIdFilter = filters?.advanceFilters?.find(f => f.properties === 'TC/TM ID');

  // TM Received Type filter
  const TMReceivedTypeFilterOptions = ['FETCHED', 'UPLOADED'];
  const TMReceivedTypeFilterObject = filters?.advanceFilters?.find((f) => f.properties === 'TM Received Type');
  const TMReceivedTypeFilter = TMReceivedTypeFilterObject?.operator === '= Equals'
    ? TMReceivedTypeFilterObject?.value
    : TMReceivedTypeFilterObject?.operator === '≠ not Equals'
      ? TMReceivedTypeFilterOptions?.filter((f) => !TMReceivedTypeFilterObject?.value.includes(f))
      : [];

  // Command Type filter
  const commandTypeOptions = ['TC', 'TM'];
  const commandTypeFilterObject = filters?.advanceFilters?.find((f) => f.properties === 'Command Type');
  const commandTypeFilter = commandTypeFilterObject?.operator === '= Equals'
    ? commandTypeFilterObject?.value
    : commandTypeFilterObject?.operator === '≠ not Equals'
      ? commandTypeOptions?.filter((f) => !commandTypeFilterObject?.value.includes(f))
      : [];

  const bandMap = { 'S': 'S-band', 'X': 'X-band' };

  const payload = {
    TMHeaderTimestampRange: {
      IsFilterApplied: !(epochSecondsStart === -1 && epochSecondsEnd === -1),
      FromTime: epochSecondsStart,
      ToTime: epochSecondsEnd,
    },
    SaIdEqualFilter: sourceFilter?.operator === '= Equals' ? sourceNumbers : [],
    SaIdNotEqualFilter: sourceFilter?.operator === '≠ not Equals' ? sourceNumbers : [],
    DaIdEqualFilter: destinationFilter?.operator === '= Equals' ? destinationNumbers : [],
    DaIdNotEqualFilter: destinationFilter?.operator === '≠ not Equals' ? destinationNumbers : [],
    TcTmIdEqualFilter: tcTmIdFilter?.operator === '= Equals' ? tcTmIdFilter?.value.map(Number) ?? [] : [],
    TcTmIdNotEqualFilter: tcTmIdFilter?.operator === '≠ not Equals' ? tcTmIdFilter?.value.map(Number) ?? [] : [],
    ContactIdEqualFilter: [],
    ContactIdNotEqualFilter: [],
    BandFilter: filters.bandsFilter?.map(band => bandMap[band] || band) || [],
    LengthRangeFilter: {
      IsFilterApplied: false,
      FromLength: 6,
      ToLength: 6
    },
    SpecificTMTypeFilter: filters?.tctmFilter || [],
    GenericTCTMTypeFilter: commandTypeFilter ?? [],
    TMReceivedTypeFilter: TMReceivedTypeFilter ?? [],
    SortByOption: {
      isApplied: false,
      ascending: false,
      byProperty: "TIMESTAMP"
    }
  };
  return payload;
};

//// ----------------------------
//// API Calls for Filters
//// ----------------------------

/**
 * Helper to validate a sessionId string
 */
export const isValidSessionId = (id: unknown): id is string => {
  return typeof id === 'string' && id.trim() !== '' && id !== 'undefined' && id !== 'null';
};

/**
 * Helper to get or initialize a valid sessionId in sessionStorage
 */
export const getOrInitSessionId = (): string => {
  const existing = sessionStorage.getItem('sessionId');
  if (isValidSessionId(existing)) {
    return existing.trim();
  }
  const newId = String(uuidv4());
  sessionStorage.setItem('sessionId', newId);
  return newId;
};

/**
 * Updates sessionId in sessionStorage from the entry object (at cursorId level)
 */
export const syncSessionIdFromResponse = (res: any): void => {
  const apiSessionId = res?.data?.[0]?.sessionId;
  if (isValidSessionId(apiSessionId)) {
    sessionStorage.setItem('sessionId', apiSessionId.trim());
  }
};

/** Builds the full filter payload and calls the console filter API */
export async function postFilters(
  filters: ConsoleQueryFilters,
  truetwin_id: string,
  sat_id: string,
  scenario_id: string,
  cursor: string,
  isFilterApplied: boolean,
  session_Id: string,
  isLoadingMore: boolean
): Promise<{ data: unknown[] }> {
  if (!truetwin_id || truetwin_id === "undefined" || truetwin_id === "null") {
    return { data: [] };
  }
  try {
    const { startTime, endTime } = filters?.timeFilter ?? {};
    const epochSecondsStart = startTime ?? -1;
    const epochSecondsEnd = endTime ?? -1;

    const sourceFilter = filters?.advanceFilters?.find(
      (f) => f.properties === "Source"
    );
    const sourceVal = sourceFilter?.value;
    const sourceNumbers = Array.isArray(sourceVal)
      ? sourceVal.map((val) => Number(String(val).split("-")[0]))
      : sourceVal != null
        ? [Number(String(sourceVal).split("-")[0])]
        : undefined;
    const destinationFilter = filters?.advanceFilters?.find(
      (f) => f.properties === "Destination"
    );
    const destVal = destinationFilter?.value;
    const destinationNumbers = Array.isArray(destVal)
      ? destVal.map((val) => Number(String(val).split("-")[0]))
      : destVal != null
        ? [Number(String(destVal).split("-")[0])]
        : undefined;
    const tcTmIdFilter = filters?.advanceFilters?.find(
      (f) => f.properties === "TC/TM ID"
    );

    const TMReceivedTypeFilterOptions = ["FETCHED", "UPLOADED"];
    const TMReceivedTypeFilterObject = filters?.advanceFilters?.find(
      (f) => f.properties === "TM Received Type"
    );
    const TMReceivedTypeFilter =
      TMReceivedTypeFilterObject?.operator === "= Equals"
        ? TMReceivedTypeFilterObject?.value
        : TMReceivedTypeFilterObject?.operator === "≠ not Equals"
          ? TMReceivedTypeFilterOptions.filter(
              (f) =>
                !(TMReceivedTypeFilterObject?.value as string[] | undefined)?.includes(
                  f
                )
            )
          : [];

    const commandTypeOptions = ["TC", "TM"];
    const commandTypeFilterObject = filters?.advanceFilters?.find(
      (f) => f.properties === "Command Type"
    );
    const commandTypeFilter =
      commandTypeFilterObject?.operator === "= Equals"
        ? commandTypeFilterObject?.value
        : commandTypeFilterObject?.operator === "≠ not Equals"
          ? commandTypeOptions.filter(
              (f) =>
                !(commandTypeFilterObject?.value as string[] | undefined)?.includes(
                  f
                )
            )
          : [];

    const selectedTestPlanResultId =
      filters?.validateSelectionFilter?.testPlanResultId?.trim();

    const selectedTestCaseResultId =
      filters?.validateSelectionFilter?.testCaseResultId?.trim();

    const selectedTestPlanId =
      filters?.validateSelectionFilter?.testPlanId?.trim();

    const selectedTestCaseId =
      filters?.validateSelectionFilter?.testCaseId?.trim();

    const planFilterValue = selectedTestPlanResultId || selectedTestPlanId;
    const caseFilterValue = selectedTestCaseResultId || selectedTestCaseId;

    const payload = {
      TMHeaderTimestampRange: {
        IsFilterApplied: !(
          epochSecondsStart === -1 && epochSecondsEnd === -1
        ),
        FromTime: epochSecondsStart,
        ToTime: epochSecondsEnd,
      },
      // Default: console should never filter by Groundstation (G) timestamps.
      // Reports can override this via `filters.payloadOverrides`.
      GSTimestampRange: {
        IsFilterApplied: false,
        FromTime: -1,
        ToTime: -1,
      },
      SaIdEqualFilter:
        sourceFilter?.operator === "= Equals" ? sourceNumbers ?? [] : [],
      SaIdNotEqualFilter:
        sourceFilter?.operator === "≠ not Equals" ? sourceNumbers ?? [] : [],
      DaIdEqualFilter:
        destinationFilter?.operator === "= Equals"
          ? destinationNumbers ?? []
          : [],
      DaIdNotEqualFilter:
        destinationFilter?.operator === "≠ not Equals"
          ? destinationNumbers ?? []
          : [],
      TcTmIdEqualFilter:
        tcTmIdFilter?.operator === "= Equals"
          ? (Array.isArray(tcTmIdFilter?.value)
              ? tcTmIdFilter.value.map(Number)
              : tcTmIdFilter?.value != null
                ? [Number(tcTmIdFilter.value)]
                : []) ?? []
          : [],
      TcTmIdNotEqualFilter:
        tcTmIdFilter?.operator === "≠ not Equals"
          ? (Array.isArray(tcTmIdFilter?.value)
              ? tcTmIdFilter.value.map(Number)
              : tcTmIdFilter?.value != null
                ? [Number(tcTmIdFilter.value)]
                : []) ?? []
          : [],
      ContactIdEqualFilter: [],
      ContactIdNotEqualFilter: [],
      BandFilter: filters?.bandsFilter ?? [],
      LengthRangeFilter: {
        IsFilterApplied: false,
        FromLength: 6,
        ToLength: 6,
      },
      SpecificTMTypeFilter: filters?.tctmFilter ?? [],
      GenericTCTMTypeFilter: commandTypeFilter ?? [],
      TMReceivedTypeFilter: TMReceivedTypeFilter ?? [],
      SortByOption: {
        isApplied: false,
        ascending: false,
        byProperty: "TIMESTAMP",
      },
      // Validate selection now filters by result ids instead of legacy plan/case ids.
      TestPlanResultIdEqualFilter: planFilterValue ? [planFilterValue] : [],
      TestCaseResultIdEqualFilter: caseFilterValue ? [caseFilterValue] : [],
      ...(selectedTestPlanId ? { TestPlanIdEqualFilter: [selectedTestPlanId] } : {}),
      ...(selectedTestCaseId ? { TestCaseIdEqualFilter: [selectedTestCaseId] } : {}),
      ...(filters?.payloadOverrides ?? {}),
    };

    const effectiveSessionId = isValidSessionId(session_Id) ? session_Id.trim() : getOrInitSessionId();

    const res = await CommandCenterService.postConsoleFilterDetails(
      sat_id,
      truetwin_id,
      scenario_id,
      cursor,
      effectiveSessionId,
      payload
    );
    syncSessionIdFromResponse(res);
    if (res?.data) {
      if (res?.data?.length === 0 && !isLoadingMore && isFilterApplied) showToastMessage('info', 'No data found');
      return { data: res?.data };
    } else {
      if (isFilterApplied && !isLoadingMore) showToastMessage('info', 'No data found');
      return { data: [] };
    }
  } catch (err) {
    showToastMessage('error', 'Something went wrong');
    return { data: [] };
  }
};

/** Fetches console entries with filters, assigns unique IDs, and returns cursor */
export async function fetchEntries(
  sat_id: string,
  truetwin_id: string,
  scenario_id: string,
  cursor: string,
  filters: ConsoleQueryFilters,
  isFilterApplied: boolean,
  setCommandLoading: (loading: boolean) => void,
  isLoadingMore: boolean
): Promise<{ data: unknown[]; cursor: string }> {
  if (!truetwin_id || truetwin_id === "undefined" || truetwin_id === "null") {
    return { data: [], cursor: "" };
  }
  try {
    const tabSessionId = getOrInitSessionId();
    if (isFilterApplied) setCommandLoading(true);
    const { data } = await postFilters(
      filters,
      truetwin_id,
      sat_id,
      scenario_id,
      cursor,
      isFilterApplied,
      tabSessionId,
      isLoadingMore
    );
    const transformedData = (data ?? []).map((entry: unknown) => {
      const e = entry as Record<string, unknown>;
      const cd = (e["CommandDetails"] as Record<string, unknown> | undefined) ?? undefined;
      const rawResult =
        (e["Result"] as unknown) ??
        (e["result"] as unknown) ??
        (e["RESULT"] as unknown) ??
        (cd?.["Result"] as unknown) ??
        (cd?.["result"] as unknown) ??
        (cd?.["RESULT"] as unknown);

      const normalizedResult =
        typeof rawResult === "boolean"
          ? (rawResult ? "pass" : "fail")
          : typeof rawResult === "string"
            ? (() => {
                const v = rawResult.trim().toLowerCase();
                if (v === "pass" || v === "passed" || v === "true") return "pass";
                if (v === "fail" || v === "failed" || v === "false") return "fail";
                if (v === "timeout" || v === "timed_out" || v === "timedout") return "timeout";
                return undefined;
              })()
            : undefined;
      const testPlanResultId =
        (e["testPlanResultId"] as string | undefined)
        ?? (e["TestPlanResultId"] as string | undefined);
      const testCaseResultId =
        (e["testCaseResultId"] as string | undefined)
        ?? (e["TestCaseResultId"] as string | undefined);
      const testPlanName =
        (e["testPlanName"] as string | undefined) ?? (e["TestPlanName"] as string | undefined);
      const testCaseName =
        (e["testCaseName"] as string | undefined) ?? (e["TestCaseName"] as string | undefined);

      return {
        ...e,
        uniqueId: uuidv4(),
        // Legacy identifiers remain available for non-grouping consumers.
        testPlan:
          (e["testPlan"] as string | undefined)
          ?? (e["testPlanId"] as string | undefined),
        testPlanResultId,
        testCaseResultId,
        testCaseId: (e["testCaseId"] as string | undefined),
        testCaseName,
        testPlanName,
        // Normalize backend result into 'pass' | 'fail' | 'timeout' for result state
        result: normalizedResult,
        isValidationAllowed: (e["IsValidationAllowed"] ?? e["isValidationAllowed"]) === "true",
      };
    });
    const firstEntry = data?.[0] as { cursorId?: string } | undefined;
    return {
      data: transformedData,
      cursor: firstEntry?.cursorId ?? "",
    };
  } catch (error: any) {
    const message = error?.response?.data?.message;
    showToastMessage("error", message || "Failed to load console entries.");
    return { data: [], cursor: "" };
  } finally {
    setCommandLoading(false);
  }
};

/** Static payload for raw bytes API (no user-configurable filters) */
const RAW_PAYLOAD = {
  TMHeaderTimestampRange: {
    IsFilterApplied: false,
    FromTime: -1,
    ToTime: -1,
  },
  GSTimestampRange: {
    IsFilterApplied: false,
    FromTime: -1,
    ToTime: -1,
  },
  SaIdEqualFilter: [],
  SaIdNotEqualFilter: [],
  DaIdEqualFilter: [],
  DaIdNotEqualFilter: [],
  TcTmIdEqualFilter: [],
  TcTmIdNotEqualFilter: [],
  ContactIdEqualFilter: [],
  ContactIdNotEqualFilter: [],
  BandFilter: [],
  LengthRangeFilter: {
    IsFilterApplied: false,
    FromLength: 6,
    ToLength: 6,
  },
  SpecificTMTypeFilter: [],
  GenericTCTMTypeFilter: [],
  TMReceivedTypeFilter: [],
  SortByOption: {
    isApplied: false,
    ascending: false,
    byProperty: "TIMESTAMP",
  },
};

/** Fetches raw bytes entries with cursor-based pagination */
export async function fetchRawEntries(
  sat_id: string,
  truetwin_id: string,
  scenario_id: string,
  cursor: string
): Promise<{ data: unknown[]; cursor: string }> {
  if (!truetwin_id || truetwin_id === "undefined" || truetwin_id === "null") {
    return { data: [], cursor: "" };
  }
  try {
    const tabSessionId = getOrInitSessionId();
    const res = await CommandCenterService.getAllRawCommandDetails(
      sat_id,
      truetwin_id,
      scenario_id,
      cursor,
      tabSessionId,
      RAW_PAYLOAD
    );
    syncSessionIdFromResponse(res);
    const list = res?.data ?? [];
    const firstEntry = list[0] as { cursorId?: string } | undefined;
    return {
      data: list,
      cursor: firstEntry?.cursorId ?? "",
    };
  } catch (error: any) {
    const message = error?.response?.data?.message;
    showToastMessage("error", message || "Failed to load raw entries.");
    return { data: [], cursor: "" };
  }
}


//// ----------------------------
//// Filter Group Utilities
//// ----------------------------

/**
 * Returns the number of header-active filter groups.
 */
export const getActiveGroupsLength = (consoleFilterGroups?: Map<string, ConsoleFilterOptionType>) => {
  if (!consoleFilterGroups) return 0;
  return Array.from(consoleFilterGroups.values()).filter(g => g.isHeaderActive).length;
};

/**
 * Generates a unique name for a new custom filter group.
 */
export const generateCustomName = (map: Map<string, ConsoleFilterOptionType>) => {
  const usedNumbers = new Set(
    Array.from(map.values())
      .map(g => g.filterGroupName.toLowerCase())
      .map(name => {
        if (name === "custom") return 0;
        const match = name.match(/^custom\s(\d+)$/);
        return match ? Number(match[1]) : null;
      })
      .filter((n): n is number => n !== null)
  );
  let next = 0;
  while (usedNumbers.has(next)) next++;
  return next === 0 ? "Custom" : `Custom ${next}`;
};

/**
 * Creates a lookup map from an array of filter options.
 */
export const createLookupMap = (filterArray = []) =>
  Object.fromEntries(filterArray.map(({ Id, Label }) => [Id, Label]));

/**
 * Creates an advanced filter object from equal/not-equal ids.
 */
const createAdvanceFilter = ({
  propertyName,
  equalIds = [],
  notEqualIds = [],
  lookupMap = {},
}) => {
  const ids = equalIds.length ? equalIds : notEqualIds;
  if (!ids.length) return null;

  const operator = equalIds.length ? "= Equals" : "≠ not Equals";
  const value =
    propertyName === "TC/TM ID"
      ? ids.map(String)
      : propertyName === "TM Received Type" ||
        propertyName === "Command Type"
        ? ids
        : ids.map((id) => lookupMap[id]).filter(Boolean);

  return { properties: propertyName, operator, value };
};

/**
 * Creates and applies filter metadata to the UI state.
 */
export const createMetaData = (
  filterData,
  isRefresh,
  setOriginalFilterMetadata,
  handleChangeFilter,
  setAllFilters,
  setRefreshLoading,
  sourceMap,
  destinationMap
) => {
  const advanceFilters = [
    ["Source", filterData?.SaIdEqualFilter, filterData?.SaIdNotEqualFilter, sourceMap],
    ["Destination", filterData?.DaIdEqualFilter, filterData?.DaIdNotEqualFilter, destinationMap],
    ["TC/TM ID", filterData?.TcTmIdEqualFilter, filterData?.TcTmIdNotEqualFilter],
    ["TM Received Type", filterData?.TMReceivedTypeFilter],
    ["Command Type", filterData?.GenericTCTMTypeFilter],
  ]
    .map(([propertyName, equalIds, notEqualIds, lookupMap]) =>
      createAdvanceFilter({ propertyName, equalIds, notEqualIds, lookupMap })
    )
    .filter(Boolean);

  const newFilterState = {
    bandsFilter: filterData?.BandFilter ?? [],
    tctmFilter: filterData?.SpecificTMTypeFilter ?? [],
    advanceFilters,
    timeFilter: {
      startTime:
        filterData?.TMHeaderTimestampRange?.FromTime === -1
          ? null
          : filterData?.TMHeaderTimestampRange?.FromTime ?? null,
      endTime:
        filterData?.TMHeaderTimestampRange?.ToTime === -1
          ? null
          : filterData?.TMHeaderTimestampRange?.ToTime ?? null,
    },
  };

  setOriginalFilterMetadata(newFilterState);
  handleChangeFilter(newFilterState);
  setAllFilters((prev) => ({
    ...prev,
    ...newFilterState,
  }));
  if (isRefresh) setRefreshLoading(false);
};

/**
 * Checks if a filter group name already exists (case-insensitive).
 */
export const checkDuplicateName = (groupName: string, consoleFilterGroups?: Map<string, ConsoleFilterOptionType>) => {
  return Array.from(consoleFilterGroups?.values() ?? []).some(
    group => group.filterGroupName?.toLowerCase() === groupName?.toLowerCase()
  );
};

/**
 * Updates a filter group's active/header state.
 */
export const updateFilterGroup = (
  id: string,
  flags: Partial<Pick<ConsoleFilterOptionType, "isActive" | "isHeaderActive">>,
  setConsoleFilterGroups
) => {
  setConsoleFilterGroups(prev => {
    const newMap = new Map(prev);
    const group = newMap.get(id);
    if (group) newMap.set(id, { ...group, ...flags });
    return newMap;
  });
};

/**
 * Handles tab removal and sets the next active tab or resets filters if none remain.
 */
export const enableNextTabAccordingly = (
  e,
  id,
  consoleFilterGroups: Map<string, ConsoleFilterOptionType>,
  setActiveFilterGroupId,
  setAllFilters,
  setOriginalFilterMetadata,
  handleChangeFilter,
  activeFilterTab,
  setConsoleFilterGroups
) => {
  e.stopPropagation();
  const groupToRemove = consoleFilterGroups.get(id);
  const headerActiveKeys = Array.from(consoleFilterGroups.entries())
    .filter(([_, g]) => g.isHeaderActive)
    .map(([key]) => key);

  const index = headerActiveKeys.indexOf(id);
  // If removed tab was active, activate neighbor
  if (groupToRemove?.isActive) {
    const previousId =
      index > 0
        ? headerActiveKeys[index - 1]
        : headerActiveKeys.length > 1
          ? headerActiveKeys[index + 1]
          : null;

    if (previousId) {
      const previousGroup = consoleFilterGroups.get(previousId);
      updateFilterGroup(previousId, { isActive: true, isHeaderActive: true }, setConsoleFilterGroups);
      setActiveFilterGroupId(previousId);
      activeFilterTab(previousId, previousGroup?.FilterMetadata || {});
    } else {
      setActiveFilterGroupId(null);

      const emptyFilters = {
        bandsFilter: [],
        tctmFilter: [],
        advanceFilters: [],
        timeFilter: { startTime: null, endTime: null },
      };
      setAllFilters(emptyFilters);
      setOriginalFilterMetadata(emptyFilters);
      handleChangeFilter(emptyFilters);
    }
  }
};